/**
 * Pure normalization/validation for the product `.xlsx` bulk import.
 *
 * Kept dependency-free (no SheetJS) so it is trivially unit-testable: the
 * dialog reads the file with SheetJS → `sheet_to_json` → passes plain row
 * objects here. There is no backend batch endpoint (FAV-130), so the dialog
 * loops per-row create/update using the normalized rows this module returns.
 *
 * Template columns (9): categoria, nome, descricao, preco,
 * tempo_preparo_min, disponivel, estoque, tags, destaque.
 */

/** Canonical template headers, in display order. */
export const TEMPLATE_COLUMNS = [
  'categoria',
  'nome',
  'descricao',
  'preco',
  'tempo_preparo_min',
  'disponivel',
  'estoque',
  'tags',
  'destaque',
] as const;

export type TemplateColumn = (typeof TEMPLATE_COLUMNS)[number];

export interface NormalizedProductRow {
  categoria: string;
  nome: string;
  descricao?: string | undefined;
  preco: number;
  tempoPreparoMin?: number | undefined;
  disponivel: boolean;
  estoque?: number | undefined;
  tags: string[];
  destaque: boolean;
}

export interface ParsedRow {
  /** 1-based data line (header row excluded). */
  line: number;
  raw: Record<string, unknown>;
  data?: NormalizedProductRow;
  errors: string[];
}

export interface ParseResult {
  rows: ParsedRow[];
  validCount: number;
  errorCount: number;
}

const stripDiacritics = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Lowercased, accent-free, underscore-joined key for fuzzy header matching. */
const normalizeKey = (header: string): string =>
  stripDiacritics(header).trim().toLowerCase().replace(/\s+/g, '_');

/** Header synonyms → canonical column. Accent-free, normalized form. */
const HEADER_ALIASES: Record<string, TemplateColumn> = {
  categoria: 'categoria',
  nome: 'nome',
  produto: 'nome',
  descricao: 'descricao',
  preco: 'preco',
  valor: 'preco',
  tempo_preparo_min: 'tempo_preparo_min',
  tempo_preparo: 'tempo_preparo_min',
  tempo_de_preparo: 'tempo_preparo_min',
  preparo: 'tempo_preparo_min',
  disponivel: 'disponivel',
  ativo: 'disponivel',
  estoque: 'estoque',
  quantidade: 'estoque',
  tags: 'tags',
  etiquetas: 'tags',
  destaque: 'destaque',
  destacado: 'destaque',
  featured: 'destaque',
};

/** Reduce a raw row (arbitrary header casing/accents) to canonical columns. */
function resolveColumns(raw: Record<string, unknown>): Partial<Record<TemplateColumn, unknown>> {
  const out: Partial<Record<TemplateColumn, unknown>> = {};
  for (const [key, value] of Object.entries(raw)) {
    const canonical = HEADER_ALIASES[normalizeKey(key)];
    // First non-empty wins so a stray duplicate blank column can't clobber data.
    if (canonical && (out[canonical] === undefined || out[canonical] === '')) {
      out[canonical] = value;
    }
  }
  return out;
}

/** Parse a money cell tolerating "29,90", "1.234,56", "R$ 10", or a number. */
export function parseBrlNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  let s = String(value).trim().replace(/r\$/i, '').replace(/\s/g, '');
  if (!s) return null;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && hasDot) s = s.replace(/\./g, '').replace(',', '.');
  else if (hasComma) s = s.replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const TRUE_WORDS = new Set([
  'sim', 's', 'true', 'verdadeiro', '1', 'x', 'yes', 'y', 'ativo', 'disponivel',
]);
const FALSE_WORDS = new Set([
  'nao', 'n', 'false', 'falso', '0', 'no', 'inativo', 'indisponivel',
]);

function parseBool(value: unknown, fallback: boolean): boolean {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const s = stripDiacritics(String(value).trim().toLowerCase());
  if (TRUE_WORDS.has(s)) return true;
  if (FALSE_WORDS.has(s)) return false;
  return fallback;
}

type OptionalInt = { ok: true; value?: number } | { ok: false };

/** Optional non-negative integer; blank → ok/undefined, garbage → not ok. */
function parseOptionalNonNegInt(value: unknown): OptionalInt {
  if (value === null || value === undefined || value === '') return { ok: true };
  const n = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'));
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return { ok: false };
  return { ok: true, value: n };
}

function parseTags(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  const s = String(value).trim();
  if (!s) return [];
  return s
    .split(/[;,]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function parseRow(raw: Record<string, unknown>, index: number): ParsedRow {
  const c = resolveColumns(raw);
  const errors: string[] = [];

  const nome = String(c.nome ?? '').trim();
  if (!nome) errors.push('Nome é obrigatório');

  const categoria = String(c.categoria ?? '').trim();
  if (!categoria) errors.push('Categoria é obrigatória');

  const preco = parseBrlNumber(c.preco);
  if (preco === null) errors.push('Preço inválido');
  else if (preco <= 0) errors.push('Preço deve ser maior que zero');

  const descricaoRaw = String(c.descricao ?? '').trim();
  const descricao = descricaoRaw || undefined;

  const tempo = parseOptionalNonNegInt(c.tempo_preparo_min);
  if (!tempo.ok) errors.push('Tempo de preparo inválido');

  const estoque = parseOptionalNonNegInt(c.estoque);
  if (!estoque.ok) errors.push('Estoque inválido');

  const disponivel = parseBool(c.disponivel, true);
  const destaque = parseBool(c.destaque, false);
  const tags = parseTags(c.tags);

  const row: ParsedRow = { line: index + 1, raw, errors };
  if (errors.length === 0 && preco !== null && tempo.ok && estoque.ok) {
    row.data = {
      categoria,
      nome,
      descricao,
      preco,
      tempoPreparoMin: tempo.value,
      disponivel,
      estoque: estoque.value,
      tags,
      destaque,
    };
  }
  return row;
}

/** Normalize + validate a list of raw spreadsheet rows. */
export function parseProductRows(rows: Array<Record<string, unknown>>): ParseResult {
  const parsed = rows.map((raw, i) => parseRow(raw, i));
  const validCount = parsed.filter((r) => r.data !== undefined).length;
  return {
    rows: parsed,
    validCount,
    errorCount: parsed.length - validCount,
  };
}
